import React, { useCallback, useEffect } from 'react';
import { useObservable } from 'react-use-observable';

import { FilterOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';

import type { FormikProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import ordersService from 'v2/services/orders';

import initialValues, {
  CustomerTypes,
  ECustomerSearchTypes,
  getInitialValues,
  InvoiceStatus,
  PaymentTypes
} from './initialValues';
import FilterSchema from './validations';

const Filter = () => {
  useEffect(() => {
    ordersService.loadOrderStatus();
  }, []);

  const [isLoading] = useObservable(() => ordersService.isLoading(), []);

  const [orderStatus] = useObservable(() => ordersService.getOrderStatus(), []);

  const onSubmit = useCallback((values: typeof initialValues, { setSubmitting }: FormikProps<typeof initialValues>) => {
    ordersService.setFilter({ ...values });
    setSubmitting(false);
  }, []);

  return (
    <Formik initialValues={initialValues} validationSchema={FilterSchema} onSubmit={onSubmit}>
      {({ isValid, isSubmitting, dirty, resetForm, values, setFieldValue }: FormikProps<typeof initialValues>) => {
        const onChangeCustomerType = (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          setFieldValue('customer', '');
          setFieldValue('customer_type', v);
        };

        const onChangeInvoice = (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          let value = null;
          if (v && !isNaN(Number(v))) value = v;
          setFieldValue('invoice_id', value);
        };

        const handleClearFilter = () => {
          const clearedValues = getInitialValues();
          resetForm({ values: clearedValues });
          ordersService.cleanFilter();
        };

        return (
          <Form id='sales-filter' className='mb-4 w-full rounded-sm bg-white p-4 sm:px-2 sm:pr-2'>
            <div>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Field label='Período' id='date' name='date' component={DateRangePickerFormField} />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Row gutter={[8, 8]}>
                    <Col xs={8}>
                      <Field
                        id='customer_type'
                        name='customer_type'
                        label='Cliente'
                        inputProps={{
                          className: ''
                        }}
                        fullWidth
                        options={CustomerTypes}
                        component={SelectFormField}
                        onChange={onChangeCustomerType}
                      />
                    </Col>
                    <Col xs={16}>
                      <Field
                        id='customer'
                        name='customer'
                        component={TextFormField}
                        inputProps={{
                          className: 'border-y border-r border-l-0'
                        }}
                        fullWidth
                        label={
                          values.customer_type == ECustomerSearchTypes.DOCUMENT
                            ? 'CPF/CNPJ'
                            : values.customer_type == ECustomerSearchTypes.EMAIL
                              ? 'E-mail'
                              : 'Nome'
                        }
                        placeholder={
                          values.customer_type == ECustomerSearchTypes.NAME
                            ? 'Insira o nome'
                            : values.customer_type == ECustomerSearchTypes.EMAIL
                              ? 'Insira o e-mail'
                              : 'Insira o CPF/CNPJ'
                        }
                        mask={values.customer_type == ECustomerSearchTypes.DOCUMENT ? 'document' : null}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Field
                    type={'tel'}
                    label='Fatura'
                    onChange={onChangeInvoice}
                    fullWidth
                    id='invoice_id'
                    name='invoice_id'
                    mask='number'
                    component={TextFormField}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Field
                    id='product_id'
                    name='product_id'
                    fullWidth
                    label='Produtos'
                    endpoint='/reports/filter-physical-products'
                    component={SelectProduct}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Field
                    id='payment_status'
                    name='payment_status'
                    multiple
                    options={InvoiceStatus}
                    fullWidth
                    label='Status da fatura'
                    component={SelectFormField}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Field
                    id='status'
                    fullWidth
                    multiple
                    name='status'
                    options={orderStatus}
                    label='Status da entrega'
                    component={SelectFormField}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Field
                    id='payment_method'
                    fullWidth
                    multiple
                    name='payment_method'
                    options={PaymentTypes}
                    label='Tipo de pagamento'
                    component={SelectFormField}
                  />
                </Col>
                <Col xs={12} md={8} className='flex items-end md:pt-5'>
                  <Button
                    id='clear-button'
                    disabled={!dirty || isSubmitting || isLoading}
                    type='text'
                    block
                    onClick={handleClearFilter}
                    loading={isLoading || isSubmitting}
                  >
                    <span id='clear-button-label'>Limpar</span>
                  </Button>
                </Col>
                <Col xs={12} md={8} className='flex items-end md:pt-5'>
                  <Button
                    id='basic-filter-button'
                    type='primary'
                    disabled={!isValid || isSubmitting || isLoading}
                    block
                    htmlType='submit'
                    loading={isLoading || isSubmitting}
                    icon={<FilterOutlined id='basic-filter-button-icon' />}
                  >
                    <span id='basic-filter-button-label'>Filtrar</span>
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
export default Filter;
